import { AsyncComponent } from '@frontend/components/common';
import { lazy } from 'react';
import { Route } from 'react-router-dom';

import { AccountInitialize } from './account/account-initialize/account-initialize.component';
import { useSSORoutes } from './account/sso/sso-routes';
import AdminRouteLayout from './admin-route-layout/admin-route-layout.component';
import { AttributeRoutes } from './attribute/attribute-routes';
import { BasketRoutes } from './basket/basket-routes';
import { BrandingRoutes } from './branding/branding-routes';
import { BudgetRoutes } from './budget/budget-routes';
import { BundleRoutes } from './bundle/bundle-routes';
import { CertificateRoutes } from './certificate/certificate-routes';
import { ColliRoutes } from './colli/colli-routes';
import { ConstraintRoutes } from './constraint/constraint-routes';
import { CostCenterRoutes } from './cost-center/cost-center-routes';
import { DeceasedRoutes } from './deceased/deceased-routes';
import { DeviceRoutes } from './device/device-routes';
import { DockerImageRoutes } from './docker-image/docker-image-routes';
import { DocumentRoutes } from './document/document-routes';
import { EntityShareRoutes } from './entity-share/entity-share-routes';
import { EquipmentGroupRoutes } from './equipment-group/equipment-group-routes';
import { EquipmentProductRoutes } from './equipment-product/equipment-product-routes';
import { FunctionalityRoutes } from './functionality/functionality-routes';
import { AdminIdentificationRoutes, IdentificationRoutes } from './identification/identification-routes';
import { ImportRoutes } from './import/import-routes';
import { InviteRoutes } from './invite/invite-routes';
import { IOTRoutes } from './iot/iot-routes';
import { LabelTemplateRoutes } from './label-template/label-template-routes';
import { LabelRoutes } from './label/label-routes';
import { ModuleTemplateRoutes } from './module-template/module-template-routes';
import { MovementRoutes } from './movement/movement-routes';
import { NotificationTemplateRoutes } from './notification-template/notification-template-routes';
import { NotificationRoutes } from './notification/notification-routes';
import { OrderRoutes } from './order/order-routes';
import { PackageRoutes } from './package/package-routes';
import { PinCodeRoutes } from './pin-code/pin-code-routes';
import { PlaylistRoutes } from './playlist/playlist-routes';
import { ProblemRoutes } from './problem/problem-routes';
import { ProductCategoryRoutes } from './product-category/product-category-routes';
import { ProductGroupRoutes } from './product-group/product-group-routes';
import { ProductKitRoutes } from './product-kit/product-kit-routes';
import { ProductRoutes } from './product/product-routes';
import { QuestionGroupRoutes } from './question-group/question-group-routes';
import { AnswerRoutes } from './question/answer/answer-routes';
import { QuestionRoutes } from './question/question-routes';
import { ReportRoutes } from './report/report-routes';
import { RequirementRoutes } from './requirement/requirement-routes';
import { ReviewRoutes } from './review/review-routes';
import { CreateRoleRoutes } from './role/create-role-routes';
import { RoleRoutes } from './role/role-routes';
import { ServiceRoutes } from './service/service-routes';
import { SettingRoutes } from './setting/setting-routes';
import { SlotTemplateRoutes } from './slot-template/slot-template-routes';
import { SpotRoutes } from './spot/spot-routes';
import { StockRoutes } from './stock/stock-routes';
import { TagRoutes } from './tag/tag-routes';
import { TransactionRuleRoutes } from './transaction-rule/transaction-rule-routes';
import { TransactionRoutes } from './transaction/transaction-routes';
import { UnknownRoutes } from './unknown/unknown-routes';
import { UserBadgeRoutes } from './user-badge/user-badge-routes';
import { UserGroupRoutes } from './user-group/user-group-routes';
import { UserInterfaceRoutes } from './user-interface/user-interface-routes';
import { UserRoutes } from './user/user-routes';
import { WizardRoutes } from './wizard/wizard-routes';
import { WorkflowRoutes } from './workflow/workflow-routes';

const Configuration = lazy(() => import('./configuration/configuration.component'));

// #region Accounts
const AccountOverview = lazy(() => import('./account/account-overview'));
const AccountCreate = lazy(() => import('./account/account-create/account-create.component'));
const AccountDetail = lazy(() => import('./account/account-detail/account-detail.component'));
const AccountUpdate = lazy(() => import('./account/account-update/account-update.component'));
// #endregion
const Troubleshoot = lazy(() => import('./troubleshoot/troubleshoot.component'));
const PermissionsList = lazy(() => import('./permissions/permissions-list.component'));

export function AdminRoutes() {
    const AnswerRoute = AnswerRoutes();
    const AttributeRoute = AttributeRoutes();
    const BrandingRoute = BrandingRoutes();
    const BudgetRoute = BudgetRoutes();
    const CertificateRoute = CertificateRoutes();
    const CostCenterRoute = CostCenterRoutes();
    const EntityShareRoute = EntityShareRoutes();
    const BundleRoute = BundleRoutes();
    const CreateRoleRoute = CreateRoleRoutes();
    const DeviceRoute = DeviceRoutes();
    const DocumentRoute = DocumentRoutes();
    const EquipmentRoute = EquipmentProductRoutes();
    const EquipmentGroupRoute = EquipmentGroupRoutes();
    const IdentificationRoute = IdentificationRoutes();
    const AdminIdentificationRoute = AdminIdentificationRoutes();
    const ImportRoute = ImportRoutes();
    const InviteRoute = InviteRoutes();
    const IoTRoutes = IOTRoutes();
    const ModuleTemplateRoute = ModuleTemplateRoutes();
    const MovementRoute = MovementRoutes();
    const NotificationTemplateRoute = NotificationTemplateRoutes();
    const NotificationRoute = NotificationRoutes();
    const LabelTemplateRoute = LabelTemplateRoutes();
    const LabelRoute = LabelRoutes();
    const ColliRoute = ColliRoutes();
    const PackageRoute = PackageRoutes();
    const PlaylistRoute = PlaylistRoutes();
    const ProblemRoute = ProblemRoutes();
    const ProductCategoryRoute = ProductCategoryRoutes();
    const ProductRoute = ProductRoutes();
    const ProductKitRoute = ProductKitRoutes();
    const ProductGroupRoute = ProductGroupRoutes();
    const QuestionRoute = QuestionRoutes();
    const QuestionGroupRoute = QuestionGroupRoutes();
    const ReportRoute = ReportRoutes();
    const ReviewRoute = ReviewRoutes();
    const RoleRoute = RoleRoutes();
    const ServiceRoute = ServiceRoutes();
    const SlotTemplateRoute = SlotTemplateRoutes();
    const SpotRoute = SpotRoutes();
    const SSORoutes = useSSORoutes();
    const StockRoute = StockRoutes();
    const TagRoute = TagRoutes();
    const PinCodeRoute = PinCodeRoutes();
    const TransactionRoute = TransactionRoutes();
    const TransactionRuleRoute = TransactionRuleRoutes();
    const UnknownRoute = UnknownRoutes();
    const UserBadgeRoute = UserBadgeRoutes();
    const UserInterfaceRoute = UserInterfaceRoutes();
    const UserRoute = UserRoutes();
    const UserGroupRoute = UserGroupRoutes();
    const WorkflowRoute = WorkflowRoutes();
    const WizardRoute = WizardRoutes();
    const FunctionalityRoute = FunctionalityRoutes();
    const settingRoutes = SettingRoutes();
    const DockerImageRoute = DockerImageRoutes();
    const OrderRoute = OrderRoutes();
    const DeceasedRoute = DeceasedRoutes();
    const BasketRoute = BasketRoutes();
    const ConstraintRoute = ConstraintRoutes();
    const RequirementRoute = RequirementRoutes();

    return (
        <Route element={<AdminRouteLayout />}>
            {settingRoutes}
            <Route
                index
                element={
                    <AsyncComponent>
                        <Configuration />
                    </AsyncComponent>
                }
            />
            <Route path='accounts'>
                {/**
    // #region Accounts
    * */}
                <Route
                    index
                    element={
                        <AsyncComponent>
                            <AccountOverview />
                        </AsyncComponent>
                    }
                />
                <Route
                    path='create'
                    element={
                        <AsyncComponent>
                            <AccountCreate />
                        </AsyncComponent>
                    }
                />
                <Route path=':accountId'>
                    <Route
                        index
                        path='detail'
                        element={
                            <AsyncComponent>
                                <AccountDetail />
                            </AsyncComponent>
                        }
                    />
                    <Route
                        path='update'
                        element={
                            <AsyncComponent>
                                <AccountUpdate />
                            </AsyncComponent>
                        }
                    />
                    <Route
                        path='initialize'
                        element={
                            <AsyncComponent>
                                <AccountInitialize />
                            </AsyncComponent>
                        }
                    />

                    {SSORoutes}

                    {FunctionalityRoute}

                    {/**
         // #region Account Entities
        * */}
                    <Route path=':entityType'>
                        <Route path=':entityId'>
                            <Route
                                path='permissions'
                                element={<PermissionsList />}
                            />
                            <Route
                                path='debug'
                                element={<Troubleshoot />}
                            />
                            {CreateRoleRoute}

                            {IdentificationRoute}
                        </Route>
                    </Route>
                    {/**
         // #region Account user groups
        * */}
                    {UserGroupRoute}
                    {/**
         // #region Account equipment groups
        * */}
                    {EquipmentGroupRoute}
                    {/**
         // #region Account Functionalities
        * */}
                    {FunctionalityRoute}
                    {/**
         // #region Account product groups
        * */}
                    {ProductGroupRoute}

                    {/**
            // #region Account budgets
        * */}
                    {BudgetRoute}
                    {TransactionRuleRoute}
                    {/**
            // #region Account documents
        * */}
                    {DocumentRoute}
                    {/**
         // #region Account cost centers
        * */}
                    {CostCenterRoute}
                    {/**
         // #region Account bundles
        * */}
                    {BundleRoute}
                    {/**
                 // #region Account  tags
                * */}
                    {TagRoute}
                    {/**
         // #region Account roles
        * */}
                    {RoleRoute}
                    {EntityShareRoute}
                    {/**
         // #region Account playlists
        * */}
                    {PlaylistRoute}
                    {/**
         // #region Account question groups
        * */}
                    {QuestionGroupRoute}
                    {/**
         // #region Account questions
        * */}
                    {QuestionRoute}
                    {/**
        // #region Account Module templates
        * */}
                    {ModuleTemplateRoute}
                    {/**
        // #region Account Slot templates
        * */}
                    {SlotTemplateRoute}
                    {/**
        // #region Account spots
        * */}
                    {SpotRoute}
                    {/**
         // #region Account equipment
        * */}
                    {EquipmentRoute}
                    {/**
         // #region Account users
        * */}
                    {UserRoute}
                    {InviteRoute}
                    {/**
         // #region Account products
        * */}
                    {ProductRoute}
                    {ProductKitRoute}
                    {ServiceRoute}
                    {/**
         // #region Account attributes
        * */}
                    {AttributeRoute}

                    {/**
         // #region Account Certificates
        * */}
                    {CertificateRoute}
                    {/**
         // #region Account Workflows
        * */}
                    {WorkflowRoute}

                    {/**
         // #region Account user interfaces
        * */}
                    {UserInterfaceRoute}
                    {/**
         // #region Account IoTs
        * */}
                    {IoTRoutes}
                    {/**
                     * // #region Account Problems
                     */}
                    {ProblemRoute}
                    {/**
         // #region Account Transactions
        * */}
                    {TransactionRoute}
                    {/**
         // #region Account PIN-codes
        * */}
                    {PinCodeRoute}
                    {/**
              // #region Account Notification Templates
             * */}
                    {NotificationTemplateRoute}
                    {/**
              // #region Account Notification
             * */}
                    {NotificationRoute}
                    {/**
         // #region Account product categories
        * */}
                    {ProductCategoryRoute}

                    {/**
         // #region Account Audit
        * */}
                    {DockerImageRoute}
                    {/**
                     * // #region Account Wizard
                     */}
                    {WizardRoute}

                    {/**
                     * #region Account order
                     */}
                    {OrderRoute}

                    {/**
                     * #region Account deceased
                     */}
                    {DeceasedRoute}

                    {/** 
         // #region Account Baskets
        * */}
                    {BasketRoute}

                    {ConstraintRoute}

                    {RequirementRoute}

                    {ReviewRoute}

                    {/**
                     * // #region Account Imports
                     */}
                    {ImportRoute}
                    {/**
                     * // #region Account Label Templates
                     */}
                    {LabelTemplateRoute}
                    {LabelRoute}
                    {/**
                     * // #region Account Reports
                     */}
                    {ReportRoute}
                    {/**
                     * // #region Account User Badges
                     */}
                    {UserBadgeRoute}
                    {/**
                     * // #region Account Identifications
                     */}
                    {AdminIdentificationRoute}
                </Route>
            </Route>
            {/**
 // #region User groups
* */}
            {UserGroupRoute}
            {/**
 // #region Equipment groups
* */}
            {EquipmentGroupRoute}
            {/**
         // #region Licenses
        * */}
            {FunctionalityRoute}
            {/**
 // #region Product groups
* */}
            {ProductGroupRoute}

            {/**
 // #region Cost centers
* */}
            {CostCenterRoute}
            {/**
 // #region Bundles
* */}
            {BundleRoute}
            {/**
    // #region Budgets
* */}
            {BudgetRoute}
            {TransactionRuleRoute}
            {/**
    // #region Document
* */}
            {DocumentRoute}
            {/**
        // #region  Tags
        * */}
            {TagRoute}
            {/*
    // #region Roles
* */}
            {RoleRoute}
            {EntityShareRoute}
            {/**
 // #region Question groups
* */}
            {QuestionGroupRoute}
            {/**
    // #region Questions
* */}
            {QuestionRoute}
            {/**
    // #region Answers
* */}
            {AnswerRoute}
            {/*
    // #region Playlists
* */}
            {PlaylistRoute}
            {/*
    // #region Spots
* */}
            {SpotRoute}
            {/*
    // #region Module templates
* */}
            {ModuleTemplateRoute}
            {/*
        {/*
    // #region Slot templates
* */}
            {SlotTemplateRoute}
            {/*
    // #region Equipment
* */}
            {EquipmentRoute}
            {/*
    // #region Transactions
* */}
            {TransactionRoute}
            {/*
    // #region PIN-codes
* */}
            {PinCodeRoute}
            {/*
    // #region Users
* */}
            {UserRoute}
            {InviteRoute}
            {AdminIdentificationRoute}
            {/*
    // #region Products
* */}
            {ProductRoute}
            {ProductKitRoute}
            {ServiceRoute}
            {/*
    // #region Reviews
* */}
            {ReviewRoute}
            {/*
    // #region Attributes
* */}
            {AttributeRoute}
            {/*
    // #region Certificates
* */}
            {CertificateRoute}
            {/*
    // #region Workflows
* */}
            {WorkflowRoute}
            {/*
    // #region Wizards
* */}
            {WizardRoute}
            {/*
    // #region User interfaces
* */}
            {UserInterfaceRoute}
            {/*
    // #region IoT
* */}
            {IoTRoutes}

            {/*
    // #region Problems
* */}
            {ProblemRoute}
            {/** *
// #region Unknowns
* */}
            {UnknownRoute}
            {/*
    // #region Devices
* */}
            {DeviceRoute}
            {/*
    // #region Stocks
* */}
            {StockRoute}
            {/*
    // #region Movements
* */}
            {MovementRoute}
            {/*
    // #region Packages
* */}
            {PackageRoute}
            {/*
    // #region Collis
* */}
            {ColliRoute}

            {ImportRoute}
            {/** 
     // #region Notification Templates
     */}
            {NotificationTemplateRoute}
            {/** 
     // #region Notifications
     */}
            {NotificationRoute}
            {/**
     // #region Label Templates
     */}
            {LabelRoute}
            {/** 
     // #region Label Templates
     */}
            {LabelTemplateRoute}
            {/** 
     // #region Product Categories
     */}
            {ProductCategoryRoute}
            {ReportRoute}
            {/**Add commentMore actions
             // #region Branding
             */}
            {BrandingRoute}
            {/**
             // #region Audit
             */}
            {DockerImageRoute}

            {/**
             * #region Order
             */}
            {OrderRoute}
            {/**
             * #region Deceased
             */}
            {DeceasedRoute}
            {/**  // #region Baskets
             */}
            {BasketRoute}

            {ConstraintRoute}

            {RequirementRoute}

            <Route path=':entityType'>
                <Route path=':entityId'>
                    <Route
                        path='permissions'
                        element={<PermissionsList />}
                    />
                    <Route
                        path='debug'
                        element={<Troubleshoot />}
                    />
                </Route>
            </Route>

            {UserBadgeRoute}
        </Route>
    );
}
